/* ============================================================================
   sk-mobile-nav.js — mobile navigation drawer (AAC, GitHub issue #387)

   No jQuery, no DOM relocation, no transitionend chains. The whole menu tree is
   already in the document (snippets/sk-mobile-nav.liquid), so opening it is a class
   toggle and every level change is a compositor-only transform.

   Ella's own handler is not disabled here: the trigger button was renamed off
   `.mobileMenu-toggle` in sections/header-mobile.liquid, so theme.js:575 binds to
   nothing and never relocates the desktop mega menu.
   ============================================================================ */
(function () {
  'use strict';

  var FOCUSABLE = 'a[href], button:not([disabled]), input, select, textarea, [tabindex]:not([tabindex="-1"])';

  /* --------------------------------------------------------------------------
     Ella's clone of the desktop mega menu into its now-dead drawer is cancelled in
     snippets/halo-sidebar-menu.liquid, not here.

     theme.js `initMultiTab` registers `document.body.addEventListener('click', ...)`
     which, on the visitor's FIRST tap anywhere on the page, runs
     `halo.initMobileMenuDefault()` — a full serialize + re-parse + insert + layout of
     the whole `#HeaderNavigation` tree (~90 links and every mega-menu tile) into
     `#navigation-mobile`. That drawer can no longer open, so the work is pure cost and
     it is a large part of what made the phone menu feel slow.

     `halo` is scoped inside theme.js and is NOT on `window`, so the method cannot be
     stubbed from here — verified in the browser, `typeof window.halo === "undefined"`.
     The clone is stopped at its target instead: the drawer's `data-navigation-mobile`
     hook is renamed, so `$('[data-navigation-mobile]')` is an empty set and jQuery
     never parses or inserts the markup.
     -------------------------------------------------------------------------- */

  function init() {
    var root = document.querySelector('[data-sk-mnav]');
    var drawer = root && root.querySelector('[data-sk-mnav-drawer]');
    var toggle = document.querySelector('[data-sk-mnav-toggle]');
    if (!root || !drawer || !toggle) return;

    var panels = {};
    var panelList = drawer.querySelectorAll('[data-sk-mnav-panel]');
    for (var i = 0; i < panelList.length; i++) {
      panels[panelList[i].getAttribute('data-sk-mnav-panel')] = panelList[i];
    }
    if (!panels.root) return;

    var currentId = 'root';
    var isOpen = false;
    var savedScrollY = 0;
    var lastFocused = null;

    /* --- level changes ---------------------------------------------------- */
    function show(id, goingBack) {
      var next = panels[id];
      if (!next || id === currentId) return;
      var prev = panels[currentId];

      if (prev) {
        prev.classList.remove('is-active', 'is-parked');
        if (!goingBack) prev.classList.add('is-parked');
      }
      next.classList.remove('is-parked');
      next.classList.add('is-active');

      var scroller = next.querySelector('[data-sk-mnav-scroll]');
      if (scroller) scroller.scrollTop = 0;

      currentId = id;
      focusPanel(next);
    }

    function resetToRoot() {
      for (var id in panels) {
        if (!Object.prototype.hasOwnProperty.call(panels, id)) continue;
        panels[id].classList.remove('is-active', 'is-parked');
        var s = panels[id].querySelector('[data-sk-mnav-scroll]');
        if (s) s.scrollTop = 0;
      }
      panels.root.classList.add('is-active');
      currentId = 'root';
    }

    /* --- page scroll lock -------------------------------------------------- */
    /* iOS Safari ignores overflow:hidden on <body>, so the position is frozen and
       restored explicitly. Without this the page behind scrolls under the drawer. */
    function lockPage() {
      savedScrollY = window.pageYOffset || document.documentElement.scrollTop || 0;
      document.body.style.top = '-' + savedScrollY + 'px';
      document.body.style.position = 'fixed';
      document.body.style.width = '100%';
      document.body.classList.add('sk-mnav-open');
    }

    function unlockPage() {
      document.body.classList.remove('sk-mnav-open');
      document.body.style.position = '';
      document.body.style.top = '';
      document.body.style.width = '';
      window.scrollTo(0, savedScrollY);
    }

    /* --- focus ------------------------------------------------------------- */
    /* Focus the panel container rather than its first control. Focusing a button
       programmatically paints it in the accent colour the instant the drawer opens,
       which reads as a pressed state nobody pressed. The panel is the standard
       dialog-entry target and Tab still lands on the first control from there. */
    function focusPanel(panel) {
      if (!isOpen || !panel) return;
      panel.setAttribute('tabindex', '-1');
      panel.focus({ preventScroll: true });
    }

    function trapFocus(event) {
      if (event.key !== 'Tab') return;
      var active = panels[currentId];
      if (!active) return;
      var items = active.querySelectorAll(FOCUSABLE);
      if (!items.length) return;
      var first = items[0];
      var last = items[items.length - 1];
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus({ preventScroll: true });
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus({ preventScroll: true });
      }
    }

    /* --- open / close ------------------------------------------------------ */
    function open() {
      if (isOpen) return;
      isOpen = true;
      lastFocused = document.activeElement;
      lockPage();
      drawer.removeAttribute('inert');
      drawer.setAttribute('aria-hidden', 'false');
      root.classList.add('is-open');
      toggle.setAttribute('aria-expanded', 'true');
      focusPanel(panels[currentId] || panels.root);
      document.addEventListener('keydown', onKeydown);
    }

    function close() {
      if (!isOpen) return;
      isOpen = false;
      root.classList.remove('is-open');
      toggle.setAttribute('aria-expanded', 'false');
      drawer.setAttribute('aria-hidden', 'true');
      drawer.setAttribute('inert', '');
      unlockPage();
      document.removeEventListener('keydown', onKeydown);
      if (lastFocused && typeof lastFocused.focus === 'function') {
        lastFocused.focus({ preventScroll: true });
      }
      /* rewind after the slide-out so the next open starts at Menu */
      window.setTimeout(function () {
        if (!isOpen) resetToRoot();
      }, 320);
    }

    function onKeydown(event) {
      if (event.key === 'Escape' || event.key === 'Esc') {
        event.preventDefault();
        close();
        return;
      }
      trapFocus(event);
    }

    /* --- wiring ------------------------------------------------------------ */
    toggle.addEventListener('click', function (event) {
      event.preventDefault();
      isOpen ? close() : open();
    });

    root.addEventListener('click', function (event) {
      var closer = event.target.closest('[data-sk-mnav-close]');
      if (closer) {
        event.preventDefault();
        close();
        return;
      }

      var forward = event.target.closest('[data-sk-mnav-to]');
      if (forward) {
        event.preventDefault();
        show(forward.getAttribute('data-sk-mnav-to'), false);
        return;
      }

      var back = event.target.closest('[data-sk-mnav-back]');
      if (back) {
        event.preventDefault();
        var panel = back.closest('[data-sk-mnav-panel]');
        var parent = panel && panel.getAttribute('data-sk-mnav-parent');
        show(parent || 'root', true);
        return;
      }

      /* a real destination: let the browser navigate, and close so a same-page
         href does not leave the drawer sitting open over the result */
      if (event.target.closest('a[href]')) close();
    });

    /* restoring from bfcache must never restore an open drawer */
    window.addEventListener('pageshow', function (event) {
      if (event.persisted && isOpen) {
        close();
        resetToRoot();
      }
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
